<template>
  <div class="fu-page" data-test="fu-page">
    <div class="fu-page__head">
      <div class="fu-page__titles">
        <nav v-if="crumbs.length" class="fu-page__crumbs" aria-label="Breadcrumb" data-test="fu-crumbs">
          <ol class="fu-page__crumb-list">
            <li v-for="(c, i) in crumbs" :key="`${i}-${c.label}`" class="fu-page__crumb">
              <span v-if="i > 0" class="fu-page__crumb-sep" aria-hidden="true">/</span>
              <router-link
                v-if="c.to && i < crumbs.length - 1"
                :to="c.to"
                class="fu-page__crumb-link"
                data-test="fu-crumb-link"
              >{{ c.label }}</router-link>
              <span
                v-else
                class="fu-page__crumb-text"
                :aria-current="i === crumbs.length - 1 ? 'page' : null"
              >{{ c.label }}</span>
            </li>
          </ol>
        </nav>
        <div v-else class="fu-page__eyebrow">{{ eyebrow }}</div>
        <h1 class="fu-page__title">{{ title }}</h1>
        <p v-if="lede" class="fu-page__lede">{{ lede }}</p>
        <slot name="meta" />
      </div>
      <div v-if="$slots.actions" class="fu-page__actions">
        <slot name="actions" />
      </div>
    </div>

    <div v-if="$slots.tabs" class="fu-page__tabs">
      <slot name="tabs" />
    </div>

    <slot />
  </div>
</template>

<script>
/**
 * The frame of a follow-ups page (the export's lines 72–110): the "Follow-ups" eyebrow, the page
 * title and its one-line description, the page's buttons on the right (`actions` slot), the tab bar
 * (`tabs` slot) and the page body (default slot).
 *
 * The app already draws its own header and sidebar around this — the export's top nav and its
 * device-preview frame are NOT built. The bottom padding leaves room for the fixed bulk bar so the
 * last row and "Show more" are never hidden under it.
 *
 * Saved-list pages (Phase 3, the export's lines 128–137) pass `crumbs` instead of the eyebrow —
 * [{ label, to? }], e.g. Follow-ups / Overdue recall / List — and a `meta` slot under the lede
 * (who made the list, when, its filters). A crumb with `to` is a router-link back to that page;
 * the last crumb is the current page and never a link. No crumbs ⇒ the eyebrow, exactly as before.
 */
export default {
  name: 'FollowupsPageShell',
  props: {
    eyebrow: { type: String, default: 'Follow-ups' },
    title: { type: String, required: true },
    lede: { type: String, default: '' },
    crumbs: { type: Array, default: () => [] },
  },
};
</script>

<style lang="scss" scoped>
@import './mixins';

.fu-page {
  @include fu-font-base;
  // Full width of the app's content area — a data table wants every pixel (Nilay, 2026-10-02).
  // The page that mounts the shell decides the gutters.
  padding: 2px 0 120px;
  min-width: 0;
}

.fu-page__head {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: $fu-space-5;
  margin-bottom: $fu-space-5;
}

.fu-page__titles {
  flex: 1 1 260px;
  min-width: 0;
}

.fu-page__eyebrow {
  @include fu-eyebrow($fu-brand, $fu-fs-micro);
  letter-spacing: 0.12em;
  margin-bottom: $fu-space-2;
}

.fu-page__crumbs {
  @include fu-eyebrow($fu-text-muted, $fu-fs-micro);
  letter-spacing: 0.12em;
  margin-bottom: $fu-space-2;
}

.fu-page__crumb-list {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.fu-page__crumb {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.fu-page__crumb-sep {
  color: $fu-neutral-300;
}

.fu-page__crumb-link {
  @include fu-focus-ring;
  border-radius: $fu-radius-xs;
  color: $fu-brand;
  text-decoration: none;
  &:hover { color: $fu-brand-800; text-decoration: underline; }
}

.fu-page__title {
  @include fu-font-display;
  margin: 0;
  font-weight: 800;
  font-size: 34px;
  letter-spacing: -0.03em;
  line-height: 1.05;
  color: $fu-text;
}

.fu-page__lede {
  margin: $fu-space-2 0 0;
  max-width: 900px;
  font-size: 15px;
  line-height: 1.5;
  color: $fu-text-muted;
}

.fu-page__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

@include fu-phone {
  .fu-page__head { gap: $fu-space-4; margin-bottom: $fu-space-4; }
  .fu-page__title { font-size: 26px; }
  .fu-page__lede { font-size: $fu-fs-body-sm; }
}
</style>
